import { useState, useEffect, useRef, useMemo } from 'react';
import { Play, Pause, Volume2, VolumeX, Home, X, RotateCcw } from 'lucide-react';
import { playAlternateInhale, playAlternateExhale, initVoices } from '@/utils/audioUtils';
import { EXERCISE_BACKGROUNDS, EXERCISE_BG_FALLBACK_COLOR, CO2_WATERMARK_LOGO } from '@/utils/exerciseBackgrounds';
import AirHungerRatingModal from './AirHungerRatingModal';
import { useLastSessionSettings } from '@/hooks/useLastSessionSettings';
import ExerciseInstructionsDrawer from './ExerciseInstructionsDrawer';
import ExerciseTopLogo from './ExerciseTopLogo';
import InhalationGentleWarning from './InhalationGentleWarning';

const AlternateNostrilModal = ({ 
  isOpen, 
  onClose, 
  onSessionComplete 
}) => {
  // Session states
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [phase, setPhase] = useState('ready'); // ready, inhale-left, exhale-right, inhale-right, exhale-left
  const [progress, setProgress] = useState(0);
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showAirHungerModal, setShowAirHungerModal] = useState(false);
  const [lastSessionId, setLastSessionId] = useState(null);
  const [pendingClose, setPendingClose] = useState(false);
  const [ratedThisSession, setRatedThisSession] = useState(false);
  
  // Session duration
  const [sessionDuration, setSessionDuration] = useState(5); // minutes
  const [remainingTime, setRemainingTime] = useState(300); // seconds
  
  // Timing presets (inhale/exhale duration)
  const timingPresets = [
    { id: '2-2', label: '2s / 2s', duration: 2 },
    { id: '3-3', label: '3s / 3s', duration: 3 },
    { id: '4-4', label: '4s / 4s', duration: 4 },
    { id: '4.5-4.5', label: '4.5s / 4.5s', duration: 4.5 },
    { id: '5-5', label: '5s / 5s', duration: 5 },
    { id: '5.5-5.5', label: '5.5s / 5.5s', duration: 5.5 },
    { id: '6-6', label: '6s / 6s', duration: 6 },
  ];
  const [selectedPreset, setSelectedPreset] = useState(timingPresets[4]); // Default 5s/5s
  
  // Refs
  const soundEnabledRef = useRef(true);
  
  // Keep soundEnabledRef in sync
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  // Restore last-used settings (session duration + phase duration preset).
  useLastSessionSettings(isOpen, 'alternate-nostril', (settings) => {
    if (typeof settings.sessionDuration === 'number') {
      setSessionDuration(settings.sessionDuration);
      setRemainingTime(settings.sessionDuration * 60);
    }
    if (typeof settings.phaseDuration === 'number') {
      const match = timingPresets.find((p) => p.duration === settings.phaseDuration);
      if (match) setSelectedPreset(match);
    }
  });
  
  // Initialize voices on mount
  useEffect(() => {
    initVoices();
    const timeout = setTimeout(() => initVoices(), 100);
    return () => clearTimeout(timeout);
  }, []);
  
  // Breathing cycle logic
  // Pattern: inhale-left -> exhale-right -> inhale-right -> exhale-left -> repeat
  useEffect(() => {
    if (!isOpen || !isActive || isPaused) return;

    const phaseDuration = selectedPreset.duration * 1000;
    const updateInterval = 50;
    let elapsed = 0;

    const phases = ['inhale-left', 'exhale-right', 'inhale-right', 'exhale-left'];
    let currentPhaseIndex = phases.indexOf(phase);
    if (currentPhaseIndex === -1) currentPhaseIndex = 0;

    const interval = setInterval(() => {
      elapsed += updateInterval;
      const newProgress = (elapsed / phaseDuration) * 100;

      if (newProgress >= 100) {
        const nextPhaseIndex = (currentPhaseIndex + 1) % phases.length;
        const nextPhaseName = phases[nextPhaseIndex];
        setPhase(nextPhaseName);
        setProgress(0);
        
        if (soundEnabledRef.current) {
          if (nextPhaseName.startsWith('inhale')) {
            playAlternateInhale(0.6);
          } else {
            playAlternateExhale(0.6);
          }
        }

        // Full cycle completed when returning to inhale-left
        if (nextPhaseIndex === 0) {
          setCyclesCompleted(prev => prev + 1);
        }
      } else {
        setProgress(newProgress);
      }
    }, updateInterval);

    return () => clearInterval(interval);
  }, [isOpen, isActive, isPaused, phase, selectedPreset]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || !isActive || isPaused) return;
    
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
      setRemainingTime(prev => {
        if (prev <= 1) {
          stopSession(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen, isActive, isPaused]);

  const startSession = () => {
    setRemainingTime(sessionDuration * 60);
    setElapsedTime(0);
    setIsActive(true);
    setIsPaused(false);
    setPhase('inhale-left');
    setProgress(0);
    setCyclesCompleted(0);
    setRatedThisSession(false);
    
    // Play initial inhale sound
    if (soundEnabledRef.current) {
      playAlternateInhale(0.6);
    }
  };

  const handleTogglePause = async () => {
    const goingToPause = !isPaused;
    setIsPaused(!isPaused);
    if (goingToPause && !ratedThisSession && elapsedTime > 10) {
      setRatedThisSession(true);
      let savedSession = null;
      if (onSessionComplete) {
        savedSession = await onSessionComplete({
          patternId: 'alternate-nostril',
          patternName: 'Respiration Narines Alternées',
          durationSeconds: elapsedTime,
          cyclesCompleted: cyclesCompleted,
          settings: { sessionDuration, phaseDuration: selectedPreset.duration },
        });
      }
      setLastSessionId(savedSession?.id || null);
      setShowAirHungerModal(true);
    }
  };

  const stopSession = async (saveSession = false) => {
    let savedSession = null;
    if (saveSession && onSessionComplete && elapsedTime > 10) {
      savedSession = await onSessionComplete({
        patternId: 'alternate-nostril',
        patternName: 'Respiration Narines Alternées',
        durationSeconds: elapsedTime,
        cyclesCompleted: cyclesCompleted,
        settings: { sessionDuration, phaseDuration: selectedPreset.duration },
      });
    }
    
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    setCyclesCompleted(0);
    setElapsedTime(0);
    return savedSession;
  };

  // Reset session (go back to settings without saving)
  const resetSession = async () => {
    const wasLongSession = elapsedTime > 10;
    let savedSession = null;
    if (wasLongSession && !ratedThisSession) {
      savedSession = await stopSession(true);
    }
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setProgress(0);
    setCyclesCompleted(0);
    setElapsedTime(0);
    setRemainingTime(sessionDuration * 60);
    if (wasLongSession && !ratedThisSession) {
      setLastSessionId(savedSession?.id || null);
      setRatedThisSession(true);
      setShowAirHungerModal(true);
    }
  };

  const applyAirHungerRecommendation = (direction) => {
    // Increase duration → higher difficulty (more CO2 buildup); decrease drops it strongly
    setSelectedPreset((prev) => {
      const currentIdx = timingPresets.findIndex((p) => p.id === prev.id);
      if (direction === 'up') {
        return timingPresets[Math.min(currentIdx + 1, timingPresets.length - 1)];
      }
      if (direction === 'down-strong') {
        return timingPresets[Math.max(currentIdx - 3, 0)];
      }
      return prev;
    });
  };

  const handleClose = async () => {
    const wasLongSession = elapsedTime > 10;
    if (wasLongSession && !ratedThisSession) {
      const saved = await stopSession(true);
      setLastSessionId(saved?.id || null);
      setPendingClose(true);
      setRatedThisSession(true);
      setShowAirHungerModal(true);
    } else {
      stopSession(false);
      onClose();
    }
  };

  const handleAirHungerClose = () => {
    setShowAirHungerModal(false);
    if (pendingClose) {
      setPendingClose(false);
      onClose();
    }
  };

  // Calculate fill levels for each nostril
  const nostrilFill = useMemo(() => {
    const left = { fill: 0, active: false, blocked: false };
    const right = { fill: 0, active: false, blocked: false };
    
    if (phase === 'inhale-left') {
      left.fill = progress; // Fill from 0 to 100
      left.active = true;
      right.blocked = true;
    } else if (phase === 'exhale-right') {
      right.fill = 100 - progress; // Empty from 100 to 0
      right.active = true;
      left.blocked = true;
    } else if (phase === 'inhale-right') {
      right.fill = progress; // Fill from 0 to 100
      right.active = true;
      left.blocked = true;
    } else if (phase === 'exhale-left') {
      left.fill = 100 - progress; // Empty from 100 to 0
      left.active = true;
      right.blocked = true;
    }
    
    return { left, right };
  }, [phase, progress]);

  // Get phase instruction text
  const getPhaseText = () => {
    switch (phase) {
      case 'inhale-left':
        return 'Inspirez par la narine gauche';
      case 'exhale-right':
        return 'Expirez par la narine droite';
      case 'inhale-right':
        return 'Inspirez par la narine droite';
      case 'exhale-left':
        return 'Expirez par la narine gauche';
      default:
        return 'Prêt';
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden"
      style={{ 
        backgroundImage: `url(${EXERCISE_BACKGROUNDS.alternateNostril})`,
        backgroundColor: '#EAF4FB',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Soft white veil to match the cabinet homepage rendering */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-[1]" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(240,247,252,0.55) 55%, rgba(214,235,246,0.55) 100%)' }} />
      {/* Floating Navigation Buttons */}
      <div className="absolute top-4 left-4 z-20">
        <button
          onClick={handleClose}
          className="p-3 bg-slate-900/10 hover:bg-slate-900/20 backdrop-blur-md rounded-full transition-colors shadow-sm"
          title="Retour aux exercices"
        >
          <Home size={20} className="text-slate-700" />
        </button>
      </div>
      
      <div className="absolute top-4 right-4 z-20">
        <button
          onClick={handleClose}
          className="p-3 bg-slate-900/10 hover:bg-slate-900/20 backdrop-blur-md rounded-full transition-colors shadow-sm"
          title="Fermer"
        >
          <X size={20} className="text-slate-700" />
        </button>
      </div>

        <ExerciseTopLogo />

      {/* Main Content */}
      <div className="relative z-10 w-full h-full overflow-y-auto px-4 pt-20 sm:pt-24 pb-8 flex flex-col lg:items-center lg:justify-center lg:pb-8">
        <div className="flex flex-col items-center my-auto gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-6 lg:items-center w-full">
          
          {/* Left Panel - Settings */}
          <div className="w-full max-w-xs order-3 lg:order-none lg:col-start-1 lg:row-start-1 lg:justify-self-end lg:w-64">
            {/* Timer displayed as a stats card (moved from the top-center overlay
                so the header stays free for the mobile brand logo — matches the
                ReduceFrequencyModal layout). */}
            <div className="bg-white/85 backdrop-blur-md rounded-xl px-3 py-3 text-center border border-slate-300 shadow-md mb-4">
              <p className="text-slate-500 text-xs mb-1">Temps restant</p>
              <p className="text-slate-800 font-bold text-2xl" style={{ fontFamily: 'system-ui' }}>
                {String(Math.floor(remainingTime / 60)).padStart(2, '0')}:{String(remainingTime % 60).padStart(2, '0')}
              </p>
            </div>
            {(!isActive || isPaused) && (
              <div className="bg-white/85 backdrop-blur-md rounded-xl px-4 py-3 shadow-md border border-slate-300">
                <h3 className="font-semibold text-slate-700 mb-3">
                  {isPaused ? 'Pause - Réglages' : 'Réglages'}
                </h3>
                <div className="flex flex-col gap-4 sm:gap-5">
                  {/* Duration */}
                  <div className="flex flex-col gap-2">
                    <span className="text-slate-600 font-medium text-sm sm:text-base">Durée</span>
                    <div className="grid grid-cols-4 gap-1.5">
                      {[5, 10, 15, 20].map((min) => (
                        <button
                          key={min}
                          onClick={() => {
                            setSessionDuration(min);
                            if (isPaused) setRemainingTime(min * 60);
                          }}
                          className={`px-1 py-2 rounded-xl text-xs font-medium transition-all text-center ${
                            sessionDuration === min
                              ? 'bg-violet-500 text-white shadow-md'
                              : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                          }`}
                        >
                          {min}m
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Timing Preset */}
                  <div className="flex flex-col gap-2">
                    <span className="text-slate-600 font-medium text-sm sm:text-base">Rythme (inspir/expir)</span>
                    <div className="grid grid-cols-2 gap-2">
                      {timingPresets.map((preset) => (
                        <button
                          key={preset.id}
                          onClick={() => setSelectedPreset(preset)}
                          className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                            selectedPreset.id === preset.id
                              ? 'bg-violet-500 text-white shadow-md'
                              : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Center Column - Dual Capsule Visualization */}
          <div className="flex flex-col items-center order-1 lg:order-none lg:col-start-2 lg:row-start-1">
            {/* Two Capsules Side by Side */}
            <div className="relative flex items-end gap-6 sm:gap-8">
              <InhalationGentleWarning visible={phase === 'inhale-left' || phase === 'inhale-right'} />
              
              {/* Left Nostril Capsule */}
              <div className="flex flex-col items-center">
                <div 
                  className={`relative w-16 sm:w-20 h-56 sm:h-72 rounded-full overflow-hidden backdrop-blur-sm border-2 shadow-2xl transition-all duration-300 ${
                    nostrilFill.left.blocked 
                      ? 'bg-white/40 border-slate-500/60' 
                      : nostrilFill.left.active 
                        ? 'bg-white/60 border-blue-500/70' 
                        : 'bg-white/60 border-slate-600/70'
                  }`}
                >
                  {/* Background glow effect */}
                  <div 
                    className="absolute inset-0 rounded-full opacity-40 transition-all duration-300"
                    style={{
                      background: nostrilFill.left.blocked 
                        ? 'radial-gradient(ellipse at center, rgba(100,116,139,0.2), transparent 70%)'
                        : `radial-gradient(ellipse at center bottom, rgba(59,130,246,0.4), transparent 70%)`
                    }}
                  />
                  
                  {/* Animated fill */}
                  {!nostrilFill.left.blocked && (
                    <div 
                      className="absolute bottom-0 left-0 right-0 rounded-b-full transition-all duration-100 ease-out"
                      style={{
                        height: `${nostrilFill.left.fill}%`,
                        background: 'linear-gradient(to top, #1d4ed8, #3b82f6, #60a5fa)',
                        boxShadow: '0 0 30px rgba(59,130,246,0.5), inset 0 0 20px rgba(59,130,246,0.3)'
                      }}
                    >
                      {/* Bubbles inside */}
                      <div className="absolute inset-0 overflow-hidden rounded-b-full">
                        {[...Array(4)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute rounded-full bg-white/40 animate-pulse"
                            style={{
                              width: `${4 + (i % 3) * 2}px`,
                              height: `${4 + (i % 3) * 2}px`,
                              left: `${20 + (i * 15)}%`,
                              bottom: `${15 + (i * 20)}%`,
                              animationDelay: `${i * 0.2}s`,
                              animationDuration: `${1.5 + (i % 2) * 0.5}s`
                            }}
                          />
                        ))}
                      </div>
                      
                      {/* Top surface shine */}
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1.5 rounded-full bg-white/50 blur-sm" />
                    </div>
                  )}
                  
                  {/* Glass reflection overlay */}
                  <div 
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)'
                    }}
                  />
                  
                  {/* Side highlight */}
                  <div className="absolute left-1.5 top-4 bottom-4 w-2 rounded-full bg-white/15 blur-sm" />
                  
                  {/* Active glow ring */}
                  {nostrilFill.left.active && !nostrilFill.left.blocked && (
                    <div 
                      className="absolute -inset-1 rounded-full opacity-60 animate-pulse"
                      style={{
                        boxShadow: '0 0 20px rgba(59,130,246,0.6), 0 0 40px rgba(59,130,246,0.3)'
                      }}
                    />
                  )}
                </div>
                
                {/* Label */}
                <span className="mt-3 text-sm font-semibold text-white/90" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                  Gauche
                </span>
              </div>
              
              {/* Right Nostril Capsule */}
              <div className="flex flex-col items-center">
                <div 
                  className={`relative w-16 sm:w-20 h-56 sm:h-72 rounded-full overflow-hidden backdrop-blur-sm border-2 shadow-2xl transition-all duration-300 ${
                    nostrilFill.right.blocked 
                      ? 'bg-white/40 border-slate-500/60' 
                      : nostrilFill.right.active 
                        ? 'bg-white/60 border-violet-500/70' 
                        : 'bg-white/60 border-slate-600/70'
                  }`}
                >
                  {/* Background glow effect */}
                  <div 
                    className="absolute inset-0 rounded-full opacity-40 transition-all duration-300"
                    style={{
                      background: nostrilFill.right.blocked 
                        ? 'radial-gradient(ellipse at center, rgba(100,116,139,0.2), transparent 70%)'
                        : `radial-gradient(ellipse at center bottom, rgba(139,92,246,0.4), transparent 70%)`
                    }}
                  />
                  
                  {/* Animated fill */}
                  {!nostrilFill.right.blocked && (
                    <div 
                      className="absolute bottom-0 left-0 right-0 rounded-b-full transition-all duration-100 ease-out"
                      style={{
                        height: `${nostrilFill.right.fill}%`,
                        background: 'linear-gradient(to top, #6d28d9, #8b5cf6, #a78bfa)',
                        boxShadow: '0 0 30px rgba(139,92,246,0.5), inset 0 0 20px rgba(139,92,246,0.3)'
                      }}
                    >
                      {/* Bubbles inside */}
                      <div className="absolute inset-0 overflow-hidden rounded-b-full">
                        {[...Array(4)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute rounded-full bg-white/40 animate-pulse"
                            style={{
                              width: `${4 + (i % 3) * 2}px`,
                              height: `${4 + (i % 3) * 2}px`,
                              left: `${20 + (i * 15)}%`,
                              bottom: `${15 + (i * 20)}%`,
                              animationDelay: `${i * 0.3}s`,
                              animationDuration: `${1.5 + (i % 2) * 0.5}s`
                            }}
                          />
                        ))}
                      </div>
                      
                      {/* Top surface shine */}
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1.5 rounded-full bg-white/50 blur-sm" />
                    </div>
                  )}
                  
                  {/* Glass reflection overlay */}
                  <div 
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)'
                    }}
                  />
                  
                  {/* Side highlight */}
                  <div className="absolute left-1.5 top-4 bottom-4 w-2 rounded-full bg-white/15 blur-sm" />
                  
                  {/* Active glow ring */}
                  {nostrilFill.right.active && !nostrilFill.right.blocked && (
                    <div 
                      className="absolute -inset-1 rounded-full opacity-60 animate-pulse"
                      style={{
                        boxShadow: '0 0 20px rgba(139,92,246,0.6), 0 0 40px rgba(139,92,246,0.3)'
                      }}
                    />
                  )}
                </div>
                
                {/* Label */}
                <span className="mt-3 text-sm font-semibold text-white/90" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                  Droite
                </span>
              </div>
            </div>

            {/* Phase indicator */}
            <div className="mt-4 bg-white/80 backdrop-blur rounded-xl px-6 py-3 shadow-lg text-center max-w-xs">
              <span className="text-base sm:text-lg font-medium text-slate-700">
                {isActive ? getPhaseText() : 'Respiration Narines Alternées'}
              </span>
              {isActive && (
                <div className="text-sm text-slate-500 mt-1">
                  Cycle {cyclesCompleted + 1}
                </div>
              )}
            </div>
            
            {/* Instructions (mobile only — desktop uses drawer) */}
            {!isActive && (
              <div className="mt-4 bg-white/70 backdrop-blur rounded-xl px-4 py-3 shadow-md max-w-sm text-center lg:hidden">
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Bouchez la narine droite, inspirez par la gauche. 
                  Puis bouchez la gauche, expirez par la droite. 
                  Inspirez par la droite, puis expirez par la gauche. Répétez.
                </p>
              </div>
            )}
          </div>

          {/* Right Panel - Controls */}
          <div className="w-full max-w-xs flex-shrink-0 flex justify-center lg:justify-start order-2 lg:order-none lg:col-start-3 lg:row-start-1 lg:justify-self-start lg:w-auto">
            <div className="flex flex-row lg:flex-col gap-3 sm:gap-4 exercise-controls-mobile-fixed lg:items-center">
              <button
                onClick={() => {
                  if (!isActive) {
                    setRemainingTime(sessionDuration * 60);
                    startSession();
                  } else {
                    handleTogglePause();
                  }
                }}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(180,165,210,0.5)' }}
                data-testid="alternate-nostril-play-btn"
              >
                {!isActive || isPaused ? (
                  <Play size={28} className="text-slate-600 ml-1 sm:w-9 sm:h-9" />
                ) : (
                  <Pause size={28} className="text-slate-600 sm:w-9 sm:h-9" />
                )}
              </button>

              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(180,165,210,0.5)' }}
                data-testid="alternate-nostril-sound-btn"
              >
                {soundEnabled ? (
                  <Volume2 size={22} className="text-slate-600 sm:w-7 sm:h-7" />
                ) : (
                  <VolumeX size={22} className="text-slate-600 sm:w-7 sm:h-7" />
                )}
              </button>
              
              {/* Reset button - only show when active */}
              {isActive && phase !== 'ready' && (
                <button
                  onClick={resetSession}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  style={{ border: '3px solid rgba(239, 68, 68, 0.5)' }}
                  data-testid="alternate-nostril-reset-btn"
                  title="Réinitialiser"
                >
                  <RotateCcw size={22} className="text-red-500 sm:w-7 sm:h-7" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Instructions drawer (desktop only) */}
      <ExerciseInstructionsDrawer testIdPrefix="alternate-nostril">
        <div className="bg-white rounded-xl p-4 shadow border border-slate-200">
          <h3 className="font-semibold text-slate-700 mb-3">Instructions</h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            Bouchez la narine droite, inspirez par la gauche.
            Puis bouchez la gauche, expirez par la droite.
            Inspirez par la droite, puis expirez par la gauche. Répétez.
          </p>
          <div className="mt-4 p-3 bg-violet-50 rounded-lg">
            <p className="text-xs text-violet-700">
              <strong>Nadi Shodhana :</strong> Équilibre les hémisphères cérébraux, calme le mental, prépare à la méditation.
            </p>
          </div>
        </div>
      </ExerciseInstructionsDrawer>

      <AirHungerRatingModal
        isOpen={showAirHungerModal}
        onClose={handleAirHungerClose}
        sessionId={lastSessionId}
        onApplyRecommendation={applyAirHungerRecommendation}
      />
    </div>
  );
};

export default AlternateNostrilModal;
